import React from 'react';
import { Form } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@oysterjs/ui/dialog';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Textarea } from '@oysterjs/ui/textarea';
import { AnimateIn } from '@oysterjs/ui/transition';

import { CopilotSubmitError } from '~/components/overage-limit-error';
import { type CaraWorkspaceFileInput } from '~/api/graphql';
import { FileInput, emptyFileInputChange, type FileInputChange } from '~/components/file';
import {
  AnimatedUploadProgress,
  extractUploadFiles
} from '~/components/cara-input/animated-upload-progress';
import { type VisibilitySelectorValue } from '~/components/visibility/visibility-selector.utils';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useCreateCaraWorkspace } from '~/components/workspaces/use-create-cara-workspace';

type TemplateWorkspaceRequest = {
  message: string;
  files: CaraWorkspaceFileInput[];
  htmlDocumentTemplateIds?: string[];
};

type TemplateWorkspaceDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  guidance?: React.ReactNode;
  instructionsLabel: string;
  instructionsDescription: string;
  instructionsRows: number;
  filesDescription: string;
  buildRequest: (message: string, files: CaraWorkspaceFileInput[]) => TemplateWorkspaceRequest;
};

export const TemplateWorkspaceDialog = (props: TemplateWorkspaceDialogProps) => {
  const { open, onClose, buildRequest } = props;
  const loadingRef = React.useRef(false);
  const [message, setMessage] = React.useState('');
  const [inputFiles, setInputFiles] = React.useState<FileInputChange>(emptyFileInputChange());
  const [loading, setLoading] = React.useState(false);
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);
  const visibilityEnabled = useVisibilityEntitlement();
  const { createWorkspace, error, clearError } = useCreateCaraWorkspace(defaultVisibility);

  const resetState = React.useCallback(() => {
    setMessage('');
    setInputFiles(emptyFileInputChange());
    setLoading(false);
    setVisibility(defaultVisibility);
    clearError();
    loadingRef.current = false;
  }, [clearError, defaultVisibility]);

  const handleClose = React.useCallback(() => {
    if (loadingRef.current) {
      return;
    }
    resetState();
    onClose();
  }, [onClose, resetState]);

  React.useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleSubmit = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const request = buildRequest(message.trim(), inputFiles.files);
      await createWorkspace(request.message, request.files, {
        htmlDocumentTemplateIds: request.htmlDocumentTemplateIds,
        visibility,
        amsContext: inputFiles.amsContext.length > 0 ? inputFiles.amsContext : undefined,
        amsRefs: inputFiles.amsRefs?.length ? inputFiles.amsRefs : undefined
      });
      resetState();
      onClose();
    } catch {
      // The shared create hook exposes server and transport errors for the dialog.
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <div className="min-w-0">
        <DialogTitle>{props.title}</DialogTitle>
        <DialogDescription>{props.description}</DialogDescription>
      </div>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <DialogBody>
          {props.guidance}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>{props.instructionsLabel}</Label>
                <Description>{props.instructionsDescription}</Description>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.currentTarget.value)}
                  rows={props.instructionsRows}
                  disabled={loading}
                />
              </Field>
              <Field>
                <Label>Reference files (optional)</Label>
                <Description>{props.filesDescription}</Description>
                <FileInput
                  multiple
                  enableExisting
                  enableAMSSearch
                  onChange={(change) => {
                    clearError();
                    setInputFiles(change);
                  }}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
          <AnimatePresence>
            {error && (
              <AnimateIn>
                <CopilotSubmitError className="mt-4" error={error} />
              </AnimateIn>
            )}
            {loading && (
              <AnimateIn>
                <AnimatedUploadProgress files={extractUploadFiles(inputFiles.files)} />
              </AnimateIn>
            )}
          </AnimatePresence>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          {visibilityEnabled ? (
            <VisibilitySplitButton
              type="submit"
              value={visibility}
              onChange={setVisibility}
              loading={loading}
            >
              Create workspace
            </VisibilitySplitButton>
          ) : (
            <Button type="submit" color="sky" loading={loading}>
              Create workspace
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};
